import type { Capability, Policy, View } from '@clawharbor/manifest';
import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import type { ToolHealth } from '../api.js';
import type { DashboardActions, DashboardState } from '../store.js';
import { DESCRIPTION_MIN, draftOf, isDirty, runOf } from '../store.js';
import { ErrorNote, When, fmtInt, fmtMs } from './bits.js';
import { TypedConfirm } from './confirm.js';
import { IssueBlock } from './issues.js';
import { InputTable, OutputFields, fieldsOf } from './schema.js';
import { Runner } from './runner.js';
import { ViewCard } from './views.js';
import { Surfaces } from './toggles.js';

/**
 * One capability, as the operator's working surface.
 *
 * The card puts the manifest's claims and the evidence for them side by side: the
 * description an agent reads, the parameters it may pass, the fields it is
 * promised back, whether the binding was ever executed against the origin, and a
 * button that executes it now. Anything the card can prove it proves; anything it
 * cannot it says it cannot — "never verified" is a state with its own wording,
 * never an empty line.
 *
 * OPEN, IT IS TWO COLUMNS AND THREE FOLDS. It used to print eleven blocks end
 * to end — title, description, surfaces, visibility, freshness, parameters,
 * output, verification, a runner, a delete box and a preview — about two
 * thousand pixels for a tool whose ordinary edit is one sentence. The left
 * column now holds what somebody actually changes and the right holds what it
 * looks like, because the preview is the consequence of the column beside it.
 * Reading a schema, testing a call and destroying a tool are three different
 * visits, so each is a disclosure that costs one line until it is wanted.
 */

/**
 * The form this tool prepares, and the one decision an operator has about it.
 *
 * A stage capability's form is the only place in the product where something is
 * SENT, so the card states the three facts that decide what happens when a
 * person presses the widget's button — the method, the host the values go to,
 * and whether the site asks for a human check — and then offers the switch.
 *
 * The switch is disabled where the answer cannot be yes, with the reason in
 * words: a third-party tenant never sends on the operator's behalf, and a relay
 * cannot pass a captcha. Those are the same two conditions the manifest's own
 * integrity check and the serving route apply; this is not a third copy of the
 * rule but a label on a control the server would refuse anyway.
 */
function FormBlock(props: {
  cap: Capability;
  ownership: Policy['ownership'];
  saving: boolean;
  actions: DashboardActions;
}): VNode | null {
  const { cap, ownership, saving, actions } = props;
  const form = cap.form;
  if (!form) return null;

  const actionHost = (() => {
    try {
      return new URL(form.action).host;
    } catch {
      return form.action;
    }
  })();
  const thirdParty = ownership === 'third-party';
  const blocked = thirdParty
    ? 'This tenant is a third party, and ClawHarbor never sends a form on somebody else’s behalf.'
    : form.captcha
      ? 'The site asks for a human check before sending, and a relay cannot pass it.'
      : null;

  return html`
    <div class="formblk">
      <p class="cardlabel">Form</p>
      <p class="formblk__facts">
        <code>${form.method}</code>${SP}to${SP}<code>${actionHost}</code>, ${form.fields.length}${SP}
        ${form.fields.length === 1 ? 'field' : 'fields'}${form.captcha
          ? html`, and the site asks for a human check first`
          : null}.
      </p>
      <label class="optrow" for=${`relay-${cap.name}`}>
        <input
          id=${`relay-${cap.name}`}
          class="optrow__box"
          type="checkbox"
          checked=${form.relay === true}
          disabled=${saving || blocked !== null}
          onChange=${(e: Event) => actions.setRelay(cap.name, (e.target as HTMLInputElement).checked)}
        />
        <span class="optrow__text">
          <span class="optrow__label">Send from the widget</span>
          <span class="optrow__gloss">
            ${blocked ??
            html`The widget's button posts the person's values through this server to${SP}
              <code>${actionHost}</code>. Off, it hands them their filled-in form and opens your page
              instead.`}
          </span>
        </span>
      </label>
    </div>
  `;
}

/**
 * Whether an agent can reach this tool at all.
 *
 * Registered on EITHER surface counts, because the two are independent
 * decisions once a person has touched them: a tool served on the operator's own
 * page and hidden from chat clients is a live tool. Absent means on — every
 * manifest written before the crawl scored anything ships everything.
 */
export function isRegistered(cap: Capability): boolean {
  const web = cap.surfaces?.web?.register !== false;
  const app = cap.surfaces?.app?.register !== false;
  return web || app;
}

/**
 * Why this tool is off, and what the crawl would have to have been wrong about.
 *
 * The score is printed against the threshold and every signal that moved it is
 * printed with its delta, because the judgement is a heuristic over a corpus of
 * other people's websites and the only person who can tell whether it was wrong
 * about THIS site is the one who owns it. "Ranked low" is not something anybody
 * can agree or disagree with; "−12, an index of the site's own news" is.
 *
 * The switch is not repeated here. It is eight lines further down the same card
 * under Surfaces, where it has always been, and a second copy of a control that
 * writes the same field is how two switches come to disagree.
 */
function Usefulness(props: { cap: Capability }): VNode | null {
  const u = props.cap.usefulness;
  if (isRegistered(props.cap)) return null;

  return html`
    <div class="offblk" role="note">
      <p class="offblk__h">This tool is switched off, so no agent can call it.</p>
      <p class="offblk__p">
        It is in the manifest and neither surface registers it: it is absent from${SP}
        <code>tools/list</code> on the MCP endpoint and from the page's own${SP}
        <code>document.modelContext</code>.${SP}
        ${u
          ? html`The crawl scored it <b>${u.score}</b> against a threshold of${SP}
              <b>${u.threshold}</b> and switched it off rather than deleting it, so this is a
              decision you can reverse — turn <b>WebMCP page</b> or <b>MCP Apps</b> back on below
              and it appears on the next <code>tools/list</code>.`
          : html`The manifest does not carry the arithmetic behind that, so this card cannot say
              which run switched it off. Turning <b>WebMCP page</b> or <b>MCP Apps</b> back on below
              serves it again from the next <code>tools/list</code>.`}
      </p>
      ${u?.reasons.length
        ? html`<ul class="offblk__reasons">
            ${u.reasons.map(
              (r) => html`<li class="offblk__reason">
                <span class=${r.delta < 0 ? 'gate__delta gate__delta--down' : 'gate__delta'}>
                  ${r.delta > 0 ? `+${r.delta}` : String(r.delta)}
                </span>
                <span class="gate__note">${r.note}</span>
                <code class="gate__signal">${r.signal}</code>
              </li>`,
            )}
          </ul>`
        : null}
    </div>
  `;
}

function Verified(props: { cap: Capability; now: number }): VNode {
  const v = props.cap.verified;
  if (!v) {
    return html`<p class="verif verif--never">
      Never verified. This binding has not been executed against the origin by the crawler, so
      nothing here has been demonstrated to work — run it below.
    </p>`;
  }
  const pct = Math.round(v.passRate * 100);
  return html`
    <p class=${pct === 100 ? 'verif' : 'verif verif--partial'}>
      Verified ${When({ iso: v.at, now: props.now, absent: 'at an unrecorded time' })}${SP}·${SP}
      ${v.samples} ${v.samples === 1 ? 'sample' : 'samples'}${SP}·${SP}${pct}% pass
      ${v.fixture ? html`${SP}·${SP}<code>${v.fixture}</code>` : null}
      ${v.lastError ? html`<span class="verif__err">last error: ${v.lastError}</span>` : null}
    </p>
  `;
}

function ToolState(props: { health: ToolHealth | undefined; now: number }): VNode {
  const h = props.health;
  if (!h) {
    return html`<span class="tstate tstate--unknown">never checked</span>`;
  }
  const label =
    h.state === 'ok'
      ? `ok · ${fmtInt(h.rows)} rows`
      : h.state === 'empty'
        ? 'returned zero rows'
        : h.state === 'error'
          ? 'error'
          : 'never checked';
  return html`
    <span class=${`tstate tstate--${h.state}`}>${label}</span>
    ${h.rotten
      ? html`<span class="tstate tstate--rotten"
          >${h.consecutiveFailures} consecutive failures</span
        >`
      : null}
    ${h.error ? html`<span class="tstate__why">${h.error}</span>` : null}
    ${html`<span class="tstate__when"
      >checked ${When({ iso: h.checkedAt, now: props.now, absent: 'never' })}</span
    >`}
  `;
}

/**
 * What this tool looks like when a chat client renders its answer.
 *
 * It used to be a tab of its own, a screen away from the tool it describes, and
 * the two halves belong together: somebody configuring a tool wants to see the
 * result of the configuring. So the preview of this tool's view sits in the
 * tool, with the slot editor for that view under it — the same editor the
 * Appearance tab carried, not a second copy of it.
 *
 * The frame is only built once the card is open, because a `details` that is
 * closed never renders its children — which is also why every tool starts
 * closed: a site with twelve tools would otherwise open twelve iframes and call
 * the data plane twelve times for a screen nobody had read yet.
 */
function Appearance(props: {
  cap: Capability;
  view: View | undefined;
  previewBase: string;
  state: DashboardState;
  actions: DashboardActions;
}): VNode | null {
  const { cap, view, previewBase, state, actions } = props;
  if (!view) return null;
  if (!cap.surfaces.app.register) {
    return html`
      <div class="appear">
        <p class="cardlabel">Appearance</p>
        <p class="appear__off">
          This tool is off on the MCP Apps surface, so no chat client draws a card for it — its
          answer arrives as plain rows. The switch is above.
        </p>
      </div>
    `;
  }

  const href = `${previewBase}/${encodeURIComponent(view.id)}?host=claude&tool=${encodeURIComponent(cap.id)}`;
  return html`
    <div class="appear">
      <div class="appear__head">
        <p class="cardlabel">Appearance</p>
        <a class="linkish" href=${href} target="_blank" rel="noopener noreferrer">
          Open in a full window
        </a>
      </div>
      <iframe
        class="appear__frame"
        src=${href}
        title=${`${cap.name} as a chat client draws it`}
        loading="lazy"
      ></iframe>
      ${ViewCard({ view, state, actions })}
    </div>
  `;
}

export function CapabilityCard(props: {
  cap: Capability;
  /** The tenant's ownership: it decides whether a form may be relayed at all. */
  ownership: Policy['ownership'];
  tenantId: string;
  /** The view this tool paints, when the manifest declares one. */
  view: View | undefined;
  /** `urls.preview` as the server named it, for the panel inside the card. */
  previewBase: string;
  state: DashboardState;
  actions: DashboardActions;
  now: number;
  issues: string[];
  health: ToolHealth | undefined;
  calls: number;
  ms: number;
}): VNode {
  const { cap, state, actions, now } = props;
  const draft = draftOf(state, cap);
  const dirty = isDirty(state, cap);
  const saving = state.savingTool[cap.name] === true;
  const short = draft.description.trim().length < DESCRIPTION_MIN;
  const confirmKey = `tool:${cap.name}`;
  const fields = fieldsOf(cap.inputSchema);

  const off = !isRegistered(cap);

  return html`
    <li
      class=${`cap2${props.issues.length ? ' cap2--faulty' : ''}${off ? ' cap2--off' : ''}`}
      id=${`cap-${cap.name}`}
    >
     <details class="cap2__fold">
      <summary class="cap2__sum">
        <span class="cap2__sumname">${cap.name}</span>
        <span class="cap2__sumwhat">${draft.title}</span>
        ${off ? html`<span class="served served--off">off</span>` : null}
        ${props.issues.length
          ? html`<span class="served served--fault">${props.issues.length} fault${props.issues.length === 1 ? '' : 's'}</span>`
          : null}
        <span class="cap2__sumcalls">
          ${fmtInt(props.calls)} ${props.calls === 1 ? 'call' : 'calls'}
        </span>
      </summary>
      <div class="cap2__top">
        <span class=${cap.kind === 'read' ? 'kind' : 'kind kind--stage'}
          >${cap.kind === 'read' ? 'read-only' : cap.kind}</span
        >
        <span class="cap2__view">view <code>${cap.view}</code></span>
        ${props.calls
          ? html`<span class="cap2__calls"
              >${fmtMs(props.ms / Math.max(1, props.calls))} average</span
            >`
          : null}
        ${ToolState({ health: props.health, now })}
      </div>

      ${IssueBlock({ tool: cap.name, messages: props.issues })}
      ${Usefulness({ cap })}

      <div class="cap2__body">
       <div class="cap2__col">
        <p class="cardlabel">Configuration</p>
      <div class="cap2__edit">
        <label class="cardlabel" for=${`title-${cap.name}`}>Title</label>
        <input
          id=${`title-${cap.name}`}
          class="cap2__title"
          type="text"
          value=${draft.title}
          disabled=${saving}
          onInput=${(e: Event) =>
            actions.editTool(cap.name, { title: (e.target as HTMLInputElement).value })}
        />

        <label class="cardlabel" for=${`desc-${cap.name}`}>
          Description — the field that decides whether an agent picks this tool
        </label>
        <textarea
          id=${`desc-${cap.name}`}
          class=${short ? 'cap2__desc cap2__desc--short' : 'cap2__desc'}
          rows="4"
          disabled=${saving}
          aria-describedby=${`desc-count-${cap.name}`}
          value=${draft.description}
          onInput=${(e: Event) =>
            actions.editTool(cap.name, { description: (e.target as HTMLTextAreaElement).value })}
        ></textarea>
        <p class="cap2__count" id=${`desc-count-${cap.name}`}>
          ${draft.description.trim().length} characters${SP}
          ${short
            ? html`<b class="cap2__short"
                >— the manifest requires ${DESCRIPTION_MIN}. Saving is blocked here rather than
                rejected by the server.</b
              >`
            : ''}
        </p>

        <div class="cap2__actions">
          <button
            type="button"
            class="btn"
            disabled=${!dirty || short || saving}
            onClick=${() => actions.saveTool(cap.name)}
          >
            ${saving ? 'Saving…' : 'Save title and description'}
          </button>
          ${dirty
            ? html`<button
                type="button"
                class="btn btn--ghost"
                disabled=${saving}
                onClick=${() => actions.resetTool(cap.name)}
              >
                Discard changes
              </button>`
            : null}
        </div>
        ${ErrorNote({ message: state.toolErrors[cap.name] ?? null })}
      </div>

      ${Surfaces({ cap, tenantId: props.tenantId, saving, actions })}

      ${FormBlock({ cap, ownership: props.ownership, saving, actions })}

      <details class="sub">
        <summary class="sub__sum">
          Parameters and output
          <span class="sub__n"
            >${fields.length} ${fields.length === 1 ? 'parameter' : 'parameters'}</span
          >
        </summary>
        <div class="cap2__schemas">
          <div class="cap2__schema">
            <p class="cardlabel">Input parameters</p>
            ${InputTable({ fields })}
          </div>
          <div class="cap2__schema">
            <p class="cardlabel">Output fields</p>
            ${OutputFields({ schema: cap.outputSchema })}
          </div>
        </div>
        ${Verified({ cap, now })}
      </details>

      <details class="sub">
        <summary class="sub__sum">Run it against the live origin</summary>
        ${Runner({ cap, run: runOf(state, cap.name), actions })}
      </details>

      <details class="sub sub--danger">
        <summary class="sub__sum">Delete this tool</summary>
      <div class="cap2__delete">
        ${TypedConfirm({
          id: confirmKey,
          phrase: cap.name,
          typed: state.confirms[confirmKey] ?? '',
          prompt: html`Removes <code>${cap.name}</code> from the manifest on disk. Every agent
            holding <code>/t/${props.tenantId}/mcp</code> loses the tool on the next${SP}
            <code>tools/list</code>, and the description above is gone with it.`,
          actionLabel: `Delete ${cap.name}`,
          busy: saving,
          onType: (value) => actions.setConfirm(confirmKey, value),
          onConfirm: () => actions.deleteTool(cap.name),
        })}
      </div>
      </details>
       </div>
       <div class="cap2__col cap2__col--appear">
        ${Appearance({
          cap,
          view: props.view,
          previewBase: props.previewBase,
          state,
          actions,
        })}
       </div>
      </div>
     </details>
    </li>
  `;
}
